import type { JsonObject } from "@/lib/api/types/json";
import type { DeviceDto } from "@/lib/api/types";
import { readDeviceDisableState } from "@/lib/devices/device-disable-state";

/**
 * Derived operational state for fleet / device surfaces (not the raw API `device.status` string).
 */
export const DeviceOperationalStatus = {
  ONLINE: "ONLINE",
  OFFLINE: "OFFLINE",
  STALE: "STALE",
  ERROR: "ERROR",
  MAINTENANCE: "MAINTENANCE",
  INGESTION_DISABLED: "INGESTION_DISABLED",
  VISUALIZATION_DISABLED: "VISUALIZATION_DISABLED",
} as const;

export type DeviceOperationalStatus =
  (typeof DeviceOperationalStatus)[keyof typeof DeviceOperationalStatus];

const MS = 1000;

/** Default expected reporting cadence when metadata omits it (seconds). */
export const DEFAULT_REPORTING_INTERVAL_S = 60;

/** ONLINE if last_seen age is below this factor × reporting interval. */
export const ONLINE_THRESHOLD_FACTOR = 2;

/** STALE window: above ONLINE ceiling, below OFFLINE floor. Uses factor × reporting interval. */
export const STALE_UPPER_FACTOR = 6;

export function getReportingIntervalSeconds(device: DeviceDto): number {
  const m = device.metadata as JsonObject | null | undefined;
  const raw = m?.reporting_interval_s;
  const n = typeof raw === "number" ? raw : typeof raw === "string" ? Number(raw) : NaN;
  if (Number.isFinite(n) && n > 0) {
    return Math.min(Math.max(n, 10), 86_400);
  }
  return DEFAULT_REPORTING_INTERVAL_S;
}

function readMaintenance(meta: JsonObject | null | undefined): boolean {
  if (!meta) return false;
  if (meta.maintenance === true) return true;
  const m = String(meta.maintenance_mode ?? "").toLowerCase();
  return m === "true" || m === "1" || m === "yes";
}

function readError(meta: JsonObject | null | undefined, apiStatus: string | null | undefined): boolean {
  const s = (apiStatus ?? "").toLowerCase();
  if (s === "error" || s === "fault" || s === "failed") return true;
  if (!meta) return false;
  const hs = String(meta.health_state ?? meta.device_health ?? "").toLowerCase();
  return hs === "error" || hs === "fault";
}

export type DeviceOperationalModel = {
  status: DeviceOperationalStatus;
  /** Age of last_seen from now; null if unknown */
  lastSeenAgeMs: number | null;
  /** Positive = overdue vs 2× reporting interval */
  delayVsOnlineMs: number | null;
  /** Human delay summary e.g. "+45s late", "on time", "no data" */
  delayLabel: string;
  reportingIntervalMs: number;
  /** Hint for optional "data rate" column */
  expectedSamplesPerSec: number | null;
};

/**
 * Maps the backend's canonical activity fields to the presentation shape used by device pages.
 * Status is intentionally not recalculated here; the API is the source of truth.
 */
export function deviceActivityModelFromApi(device: DeviceDto): DeviceOperationalModel {
  const rawStatus = String(device.activity_status ?? "OFFLINE").toUpperCase();
  const knownStatuses = new Set<string>(Object.values(DeviceOperationalStatus));
  const status = (knownStatuses.has(rawStatus) ? rawStatus : DeviceOperationalStatus.OFFLINE) as DeviceOperationalStatus;
  const lastSeen = device.activity_last_seen ?? device.last_seen;
  const intervalSec = getReportingIntervalSeconds(device);
  const rawRate = (device.metadata as JsonObject | null | undefined)?.sampling_rate_hz;
  const hz = typeof rawRate === "number" ? rawRate : typeof rawRate === "string" ? Number(rawRate) : NaN;

  return {
    status,
    lastSeenAgeMs: lastSeen ? Math.max(0, Date.now() - new Date(lastSeen).getTime()) : null,
    delayVsOnlineMs: null,
    delayLabel: device.activity_delay_label ?? "No data",
    reportingIntervalMs: intervalSec * MS,
    expectedSamplesPerSec: Number.isFinite(hz) && hz > 0 ? hz : null,
  };
}

/**
 * Combines catalog fields, metadata, optional live presence (WS), and wall clock.
 */
export function computeDeviceOperationalModel(
  device: DeviceDto,
  options?: { livePresence?: string | null; nowMs?: number },
): DeviceOperationalModel {
  const now = options?.nowMs ?? Date.now();
  const meta = device.metadata as JsonObject | null | undefined;
  const disableState = readDeviceDisableState(device);
  const intervalSec = getReportingIntervalSeconds(device);
  const reportingIntervalMs = intervalSec * MS;
  const onlineMaxMs = ONLINE_THRESHOLD_FACTOR * reportingIntervalMs;
  const offlineMinMs = STALE_UPPER_FACTOR * reportingIntervalMs;

  const rawRate = meta?.sampling_rate_hz ?? meta?.data_rate_hz;
  const hz =
    typeof rawRate === "number"
      ? rawRate
      : typeof rawRate === "string"
        ? Number(rawRate)
        : NaN;
  const expectedSamplesPerSec = Number.isFinite(hz) && hz > 0 ? hz : null;

  if (disableState?.mode === "ingestion") {
    return {
      status: DeviceOperationalStatus.INGESTION_DISABLED,
      lastSeenAgeMs: device.last_seen ? now - new Date(device.last_seen).getTime() : null,
      delayVsOnlineMs: null,
      delayLabel: "Ingestion disabled",
      reportingIntervalMs,
      expectedSamplesPerSec,
    };
  }

  if (disableState?.mode === "visualization") {
    return {
      status: DeviceOperationalStatus.VISUALIZATION_DISABLED,
      lastSeenAgeMs: device.last_seen ? now - new Date(device.last_seen).getTime() : null,
      delayVsOnlineMs: null,
      delayLabel: "Visualization disabled",
      reportingIntervalMs,
      expectedSamplesPerSec,
    };
  }

  if (readMaintenance(meta)) {
    return {
      status: DeviceOperationalStatus.MAINTENANCE,
      lastSeenAgeMs: null,
      delayVsOnlineMs: null,
      delayLabel: "Maintenance window",
      reportingIntervalMs,
      expectedSamplesPerSec,
    };
  }

  if (readError(meta, device.status)) {
    return {
      status: DeviceOperationalStatus.ERROR,
      lastSeenAgeMs: device.last_seen ? now - new Date(device.last_seen).getTime() : null,
      delayVsOnlineMs: null,
      delayLabel: "Fault reported",
      reportingIntervalMs,
      expectedSamplesPerSec,
    };
  }

  const live = options?.livePresence?.toLowerCase();
  if (live === "offline" || live === "inactive") {
    return {
      status: DeviceOperationalStatus.OFFLINE,
      lastSeenAgeMs: device.last_seen ? now - new Date(device.last_seen).getTime() : null,
      delayVsOnlineMs: null,
      delayLabel: "Live: offline",
      reportingIntervalMs,
      expectedSamplesPerSec,
    };
  }

  if (!device.last_seen) {
    return {
      status: DeviceOperationalStatus.OFFLINE,
      lastSeenAgeMs: null,
      delayVsOnlineMs: null,
      delayLabel: "No data",
      reportingIntervalMs,
      expectedSamplesPerSec,
    };
  }

  const lastMs = new Date(device.last_seen).getTime();
  if (Number.isNaN(lastMs)) {
    return {
      status: DeviceOperationalStatus.OFFLINE,
      lastSeenAgeMs: null,
      delayVsOnlineMs: null,
      delayLabel: "Invalid last_seen",
      reportingIntervalMs,
      expectedSamplesPerSec,
    };
  }

  const ageMs = Math.max(0, now - lastMs);

  if (live === "online" || live === "active") {
    if (ageMs <= onlineMaxMs) {
      return {
        status: DeviceOperationalStatus.ONLINE,
        lastSeenAgeMs: ageMs,
        delayVsOnlineMs: Math.max(0, ageMs - onlineMaxMs),
        delayLabel: ageMs <= reportingIntervalMs ? "On time (live)" : "Live · within SLA",
        reportingIntervalMs,
        expectedSamplesPerSec,
      };
    }
    if (ageMs < offlineMinMs) {
      const lateMs = ageMs - onlineMaxMs;
      return {
        status: DeviceOperationalStatus.STALE,
        lastSeenAgeMs: ageMs,
        delayVsOnlineMs: lateMs,
        delayLabel: formatLate(lateMs),
        reportingIntervalMs,
        expectedSamplesPerSec,
      };
    }
    return {
      status: DeviceOperationalStatus.OFFLINE,
      lastSeenAgeMs: ageMs,
      delayVsOnlineMs: ageMs - onlineMaxMs,
      delayLabel: formatLate(ageMs - onlineMaxMs),
      reportingIntervalMs,
      expectedSamplesPerSec,
    };
  }

  if (ageMs <= onlineMaxMs) {
    return {
      status: DeviceOperationalStatus.ONLINE,
      lastSeenAgeMs: ageMs,
      delayVsOnlineMs: Math.max(0, ageMs - onlineMaxMs),
      delayLabel: ageMs < 2000 ? "On time" : formatLate(Math.max(0, ageMs - onlineMaxMs)),
      reportingIntervalMs,
      expectedSamplesPerSec,
    };
  }

  if (ageMs < offlineMinMs) {
    const lateMs = ageMs - onlineMaxMs;
    return {
      status: DeviceOperationalStatus.STALE,
      lastSeenAgeMs: ageMs,
      delayVsOnlineMs: lateMs,
      delayLabel: formatLate(lateMs),
      reportingIntervalMs,
      expectedSamplesPerSec,
    };
  }

  return {
    status: DeviceOperationalStatus.OFFLINE,
    lastSeenAgeMs: ageMs,
    delayVsOnlineMs: ageMs - onlineMaxMs,
    delayLabel: formatLate(ageMs - onlineMaxMs),
    reportingIntervalMs,
    expectedSamplesPerSec,
  };
}

function formatLate(deltaMs: number): string {
  if (deltaMs <= 0) return "On time";
  const s = Math.round(deltaMs / MS);
  if (s < 60) return `+${s}s late`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return r > 0 ? `+${m}m ${r}s late` : `+${m}m late`;
}

export function operationalStatusToIndicatorVariant(
  s: DeviceOperationalStatus,
): "online" | "offline" | "warning" | "error" | "neutral" | "accent" {
  switch (s) {
    case DeviceOperationalStatus.ONLINE:
      return "online";
    case DeviceOperationalStatus.OFFLINE:
      return "offline";
    case DeviceOperationalStatus.STALE:
      return "warning";
    case DeviceOperationalStatus.ERROR:
      return "error";
    case DeviceOperationalStatus.MAINTENANCE:
      return "accent";
    case DeviceOperationalStatus.VISUALIZATION_DISABLED:
      return "warning";
    case DeviceOperationalStatus.INGESTION_DISABLED:
      return "error";
    default:
      return "neutral";
  }
}

export function operationalStatusToCardTone(s: DeviceOperationalStatus | string | null | undefined): string {
  switch (s) {
    case DeviceOperationalStatus.ONLINE:
      return "border-status-success-border bg-status-success-surface";
    case DeviceOperationalStatus.STALE:
      return "border-status-warning-border bg-status-warning-surface";
    case DeviceOperationalStatus.ERROR:
      return "border-status-error-border bg-status-error-surface";
    case DeviceOperationalStatus.MAINTENANCE:
      return "border-accent/30 bg-accent-muted";
    case DeviceOperationalStatus.VISUALIZATION_DISABLED:
      return "border-status-warning-border bg-status-warning-surface";
    case DeviceOperationalStatus.INGESTION_DISABLED:
      return "border-status-error-border bg-status-error-surface";
    case DeviceOperationalStatus.OFFLINE:
      return "border-card-border bg-surface-primary";
    default:
      return "border-card-border bg-surface-primary";
  }
}

/** Lowercase API filter token (query param). */
export function operationalStatusToFilterToken(s: DeviceOperationalStatus): string {
  return s.toLowerCase();
}

export function formatDeviceOperationalStatusLabel(status: DeviceOperationalStatus | string | null | undefined): string {
  switch (status) {
    case DeviceOperationalStatus.ONLINE:
      return "Online";
    case DeviceOperationalStatus.OFFLINE:
      return "Offline";
    case DeviceOperationalStatus.STALE:
      return "Stale";
    case DeviceOperationalStatus.ERROR:
      return "Error";
    case DeviceOperationalStatus.MAINTENANCE:
      return "Maintenance";
    case DeviceOperationalStatus.VISUALIZATION_DISABLED:
      return "Visualization disabled";
    case DeviceOperationalStatus.INGESTION_DISABLED:
      return "Ingestion disabled";
    default:
      return typeof status === "string" && status.trim() ? status : "Unknown";
  }
}
